/**
 * RunSequence — horizontal phase stepper for a single workshop.
 *
 * Ports the legacy `_legacy/wd.html` Run Sequence panel (lines 482-637)
 * verbatim in structure:
 *
 *   .panel
 *     .panel-header   ("Run Sequence" title + ··· menu)
 *     .panel-body
 *       .phase-badge-row   ("Phase" muted pill + active phase pill)
 *       .phase-steps       (horizontal stepper: dot + label per phase)
 *       .objectives-section (objective items for the selected phase)
 *
 * Phase data lives in `run-sequence-content.ts` (WS4 real ported from
 * Loveable v1; other workshops carry em-dash placeholders).
 */

import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  CheckCircle2,
  Play,
  Undo2,
} from "lucide-react";
import type { Workshop } from "@/content/workshops";
import { useWorkshopModals } from "@/features/workshop/modals/useWorkshopModals";
import { useProgress, resolveStatus } from "@/stores/useProgress";
import { cascadeActivities } from "@/lib/workshop-completion";
import { usePowerUser } from "@/stores/usePowerUser";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import {
  resolvePhaseObjectives,
  type ObjectiveLink,
} from "@/features/workshop/overrides/workshop-override-content";
import { downloadDoc } from "@/lib/docs-api";
import {
  PHASES_BY_WORKSHOP,
  deriveWorkshopStatus,
  normalizePhaseStatuses,
  type PhaseDef,
} from "./run-sequence-content";

interface Props {
  workshopId: Workshop["id"];
}

function phaseDotClass(status: PhaseDef["status"]): string {
  if (status === "complete") return "phase-dot done";
  if (status === "active") return "phase-dot active";
  return "phase-dot locked";
}

function phaseLabelClass(status: PhaseDef["status"]): string {
  if (status === "complete") return "phase-label done";
  if (status === "active") return "phase-label active";
  return "phase-label";
}

export function RunSequence({ workshopId }: Props) {
  const phaseStatusOverrides = useProgress((s) => s.phaseStatus);
  const setPhaseStatuses = useProgress((s) => s.setPhaseStatuses);
  const setWorkshopStatus = useProgress((s) => s.setWorkshopStatus);
  // Effective phase list: each phase's status is the facilitator
  // override if present, else the static default. Everything downstream
  // (activePhase, the stepper, the selected phase) reads from this so a
  // "mark complete" / advance flows through automatically.
  const phases = (PHASES_BY_WORKSHOP[workshopId] ?? []).map((p) => ({
    ...p,
    status: resolveStatus(phaseStatusOverrides, p.id, p.status),
  }));
  const activePhase = phases.find((p) => p.status === "active");
  const allComplete =
    phases.length > 0 && phases.every((p) => p.status === "complete");
  // Default selection: the active phase, else — when everything is done
  // (no active marker) — the FINAL phase, so finishing a workshop leaves
  // you on the last step rather than snapping back to the first.
  const defaultId =
    activePhase?.id ??
    (allComplete ? phases[phases.length - 1]?.id : phases[0]?.id) ??
    null;
  const [selectedId, setSelectedId] = useState<string | null>(defaultId);
  const openModal = useWorkshopModals((s) => s.open);
  const navigate = useNavigate();
  // Power-user objective overrides (text + per-objective link target).
  const override = useEffectiveWorkshopOverride(workshopId);

  // Dispatch an objective's link: an in-app modal, another page on the site
  // (route), an external website, or an uploaded file (fetched with the
  // caller's token and opened in a new tab).
  async function handleObjectiveLink(link: ObjectiveLink): Promise<void> {
    if (
      link.kind === "modal" &&
      (link.modal === "stress-test" || link.modal === "handshake")
    ) {
      // Activity boards are routed pages, not Dialogs — overlaying them
      // on the dashboard let the grid bleed through and under OS-scale
      // compensation the Dialog only covered ~1/osDpr of the band (UAT:
      // half-screen whiteboard / stress-test).
      navigate(
        link.modal === "handshake"
          ? `/workshop/${workshopId}/handshake`
          : `/workshop/${workshopId}/stress-test`,
      );
    } else if (link.kind === "modal") {
      openModal(link.modal);
    } else if (link.kind === "route") {
      navigate(link.route);
    } else if (link.kind === "url") {
      window.open(link.url, "_blank", "noopener,noreferrer");
    } else {
      try {
        const blob = await downloadDoc("workshops", link.upload.path);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        // Revoke after a grace period so the new tab has time to load it.
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch {
        /* swallow — the file may have been removed; nothing to open */
      }
    }
  }

  // RT-GATING — the per-phase complete/reopen toggle requires power-user
  // (admin) mode, the SAME gate as Manage Crew. A role alone does not
  // unlock it: a facilitator must enter admin mode to mutate program
  // state. Everyone else sees the stepper + objectives read-only; the
  // objective "OPEN" launchers stay ungated (they open read surfaces).
  const canManageProgram = usePowerUser((s) => s.isUnlocked);

  // Reset the selected phase when navigating between workshops so we
  // don't show "no objectives" for a phase id that doesn't exist in the
  // new workshop's roster.
  useEffect(() => {
    setSelectedId(defaultId);
  }, [defaultId]);

  if (phases.length === 0) {
    return (
      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">
            <Play
              className="panel-title-icon"
              aria-hidden
              strokeWidth={0}
              style={{ fill: "currentColor" }}
            />
            Run Sequence
          </div>
          <div className="panel-menu" aria-hidden>···</div>
        </div>
        <div className="panel-body" style={{ padding: "12px 14px" }}>
          <p
            className="font-mono uppercase"
            style={{
              fontSize: "var(--fs-label)",
              color: "var(--text-muted)",
              letterSpacing: "0.18em",
            }}
          >
            Phases pending facilitator setup
          </p>
        </div>
      </div>
    );
  }

  const selected = phases.find((p) => p.id === selectedId) ?? phases[0];
  // Effective objectives = power-user override for the phase, else the static
  // defaults. Each carries an optional typed link (modal / route / url / file).
  const selectedObjectives = selected
    ? resolvePhaseObjectives(override, selected.id, selected.objectives ?? [])
    : [];

  // Recompute the workshop chip from the current (post-update) phase
  // overrides and write it. zustand `set` is synchronous, so reading
  // getState() here sees whatever a caller just wrote. Keeping the
  // workshop status purely derived is what stops the navigator badge
  // and the phase stepper from ever disagreeing.
  function syncWorkshopStatus(): void {
    const overrides = useProgress.getState().phaseStatus;
    const raw = PHASES_BY_WORKSHOP[workshopId] ?? [];
    const effective = raw.map((p) => resolveStatus(overrides, p.id, p.status));
    setWorkshopStatus(workshopId, deriveWorkshopStatus(effective));
  }

  // The set of currently-complete phase ids — the single source of
  // truth the stepper is normalised from. "active"/"upcoming" are always
  // derived from this, never toggled directly (see normalizePhaseStatuses).
  const orderedIds = phases.map((p) => p.id);
  const completeIds = new Set(
    phases.filter((p) => p.status === "complete").map((p) => p.id),
  );

  // Write a fresh complete-set to the store: re-derive every phase's
  // status (one active = first incomplete) and the workshop chip, in a
  // single batched persist.
  function applyCompleteSet(nextComplete: Set<string>): void {
    if (orderedIds.length === 0) return;
    const next = normalizePhaseStatuses(orderedIds, nextComplete);
    setPhaseStatuses(next);
    syncWorkshopStatus();
    // Cross-surface invariant: when completing / reopening phases crosses the
    // "whole workshop done" boundary, cascade the Mission Control activities
    // to match — otherwise the navigator badge (phase-derived) flips to "done"
    // while the activity cards stay stale. Activities are a separate
    // decomposition with their own ids, so they can only be coupled at this
    // boundary; partial phase progress stays independent of partial activity
    // progress. `allComplete` is the pre-toggle state (closure).
    const isDone = deriveWorkshopStatus(Object.values(next)) === "done";
    if (isDone !== allComplete) cascadeActivities(workshopId, isDone);
  }

  // Facilitator control: toggle the selected phase's completion BOTH
  // ways. We only ever flip membership in the complete-set; the blue
  // "active" marker then re-derives to the earliest incomplete phase —
  // so completing advances it, un-completing falls it back, and
  // reopening a middle phase (with later ones still complete) is handled
  // for free.
  function togglePhase(phase: PhaseDef): void {
    const next = new Set(completeIds);
    if (phase.status === "complete") next.delete(phase.id);
    else next.add(phase.id);
    applyCompleteSet(next);
  }

  // Shared chrome for the per-phase action button that sits under the
  // objectives. Green = the "complete" CTA, muted = an undo / reopen.
  // The whole-workshop sign-off lives in the HudHeader (in-session) and
  // the Mission Control panel (pre-entry); completing the final phase
  // here cascades to "done" on its own, so no bulk button is needed.
  const actionBtnBase: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 8,
    fontSize: "var(--fs-label)",
    letterSpacing: "1.5px",
    cursor: "pointer",
  };
  const greenBtn: CSSProperties = {
    ...actionBtnBase,
    background: "rgba(var(--accent-green-rgb), 0.08)",
    border: "1px solid rgba(var(--accent-green-rgb), 0.35)",
    color: "var(--accent-green)",
  };
  const mutedBtn: CSSProperties = {
    ...actionBtnBase,
    background: "transparent",
    border: "1px solid hsl(var(--border))",
    color: "var(--text-muted)",
  };

  return (
    <div className="panel">
      <div className="panel-header">
        <div className="panel-title">
          {/* Filled play triangle in true-blue: ports the legacy `▶`
              character used at `_legacy/wd.html` line 484. `fill` on
              `currentColor` gives the solid triangle, not the outline
              shape Lucide ships by default. */}
          <Play
            className="panel-title-icon"
            aria-hidden
            strokeWidth={0}
            style={{ fill: "currentColor" }}
          />
          Run Sequence
        </div>
        <div className="panel-menu" aria-hidden>···</div>
      </div>
      <div className="panel-body">
        {/* Phase badge row */}
        <div className="phase-badge-row">
          <span className="phase-badge muted">Phase</span>
          {activePhase && <span className="phase-badge">{activePhase.title}</span>}
        </div>

        {/* Horizontal stepper */}
        {/* `role="tablist"` was originally on this row, but the children
            are step buttons (with `aria-pressed`), not WAI-ARIA tabs —
            axe-core's `aria-required-children` flagged it. Honest
            semantic now: plain group with an aria-label. */}
        <div
          className="phase-steps"
          role="group"
          aria-label="Workshop phases"
        >
          {phases.map((p) => {
            // WD-3: the selected phase gets a visible ring on the dot and
            // emphasis on its label (see `.phase-dot.selected` in
            // index.css). `data-selected` is the stable test hook.
            const isSelected = selected?.id === p.id;
            return (
              <div
                key={p.id}
                className="phase-step"
                data-testid={`phase-step-${p.id}`}
              >
                <button
                  type="button"
                  className={`${phaseDotClass(p.status)}${isSelected ? " selected" : ""}`}
                  onClick={() => setSelectedId(p.id)}
                  aria-pressed={isSelected}
                  aria-label={`${p.title}: ${p.status}`}
                  data-testid={`phase-dot-${p.id}`}
                  data-selected={isSelected ? "true" : undefined}
                >
                  {p.status === "complete" && <Check aria-hidden />}
                </button>
                <button
                  type="button"
                  className={`${phaseLabelClass(p.status)}${isSelected ? " selected" : ""}`}
                  onClick={() => setSelectedId(p.id)}
                  style={{ background: "none", border: 0, padding: 0 }}
                >
                  {p.title}
                </button>
              </div>
            );
          })}
        </div>

        {/* Objectives section — bound to the selected phase */}
        <div className="objectives-section">
          <div className="objectives-title">Objectives</div>
          {selectedObjectives.length === 0 ? (
            <p
              className="font-mono"
              style={{
                fontSize: "var(--fs-base)",
                color: "var(--text-muted)",
                padding: "8px 12px",
              }}
            >
              No objectives configured.
            </p>
          ) : (
            selectedObjectives.map((obj, i) => {
              const done = selected?.status === "complete";
              const link = obj.link;
              const text = obj.text;
              const isLaunchable = link !== undefined;
              return (
                <div
                  key={`${selected?.id}-obj-${i}`}
                  className="objective-item"
                  onClick={link ? () => void handleObjectiveLink(link) : undefined}
                  role={isLaunchable ? "button" : undefined}
                  tabIndex={isLaunchable ? 0 : undefined}
                  onKeyDown={
                    link
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            void handleObjectiveLink(link);
                          }
                        }
                      : undefined
                  }
                  style={isLaunchable ? { cursor: "pointer" } : undefined}
                >
                  <div className="objective-left">
                    <span className={done ? "obj-dot-done" : "obj-dot-open"} aria-hidden />
                    <span className="objective-text">{text}</span>
                  </div>
                  {done ? (
                    <span className="obj-check" aria-hidden>
                      <Check strokeWidth={2.5} />
                    </span>
                  ) : isLaunchable ? (
                    <span
                      aria-hidden
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        color: "var(--accent-true-blue)",
                        fontSize: "var(--fs-label)",
                        fontWeight: 600,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      OPEN
                      <ArrowUpRight aria-hidden style={{ width: 11, height: 11 }} />
                    </span>
                  ) : null}
                </div>
              );
            })
          )}
          {selected && canManageProgram && (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {/* Per-phase toggle — completes / reopens the selected phase.
                  Gated (RT-GATING): only a power-user or a manage:program
                  role can flip program state. */}
              <button
                type="button"
                onClick={() => togglePhase(selected)}
                className="font-sans font-bold uppercase"
                style={selected.status === "complete" ? mutedBtn : greenBtn}
              >
                {selected.status === "complete" ? (
                  <>
                    <Undo2
                      aria-hidden
                      style={{ width: 13, height: 13 }}
                      strokeWidth={2}
                    />
                    Mark phase incomplete
                  </>
                ) : (
                  <>
                    <CheckCircle2
                      aria-hidden
                      style={{ width: 13, height: 13 }}
                      strokeWidth={2}
                    />
                    Mark phase complete
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
